import type { UseFormReturn } from 'react-hook-form';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  useIsMobile,
} from '@design-system';
import { QuickPlanPersonFields, type QuickPlanFormValues } from '@/features/client-quick-plan';


interface QuickPlanPartnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<QuickPlanFormValues>;
  onMaritalStatusChange: (value: string) => void;
  partnerLabel: string;
  onDone: () => void;
  onCancel: () => void;
  isPartnerValid: boolean;
}

export function QuickPlanPartnerDialog({
  open,
  onOpenChange,
  form,
  partnerLabel,
  onMaritalStatusChange,
  onDone,
  onCancel,
  isPartnerValid,
}: QuickPlanPartnerDialogProps) {
  const isMobile = useIsMobile();

  const capitalizedLabel = partnerLabel.charAt(0).toUpperCase() + partnerLabel.slice(1);
  const title = `${capitalizedLabel} Details`;
  const description = `Enter your client's ${partnerLabel} information`;

  const actions = (
    <div className="flex w-full gap-3">
      <Button
        type="button"
        variant="outline"
        className="flex-1"
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        type="button"
        className="flex-1"
        disabled={!isPartnerValid}
        onClick={onDone}
      >
        Done
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
      >
        <DrawerContent className="z-60 max-h-[85vh] overflow-y-auto px-4">
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <div className="space-y-6 py-4">
            <QuickPlanPersonFields
              control={form.control}
              fieldPrefix="partner."
              onMaritalStatusChange={onMaritalStatusChange}
            />
          </div>
          <DrawerFooter className="px-0">{actions}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="z-60 max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="mb-2 space-y-6">
          <QuickPlanPersonFields
            control={form.control}
            fieldPrefix="partner."
            onMaritalStatusChange={onMaritalStatusChange}
          />
        </div>
        {actions}
      </DialogContent>
    </Dialog>
  );
}
